import { TableChart, TableRows } from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    Box,
    Chip,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import getEmployees from '@src/APIs/graphQl/Employee/getEmployees';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import type { EmployeeBadge, MvpEmployeeData } from '@src/App/Components/Core/Cards/MvpEmployeeCard/MvpEmployeeCard';
import MvpEmployeeCard from '@src/App/Components/Core/Cards/MvpEmployeeCard/MvpEmployeeCard';
import { Container } from '@src/App/Components/Core/Layouts/LayoutElements/Container/Container';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import { EmployeePicture } from '@src/App/Components/General/EmployeePicture/EmployeePicture';
import ExpandableText from '@src/App/Components/General/ExpandableText/ExpandableText';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import EmployeeOverlay from '@src/App/Components/Overlays/EmployeeOverlay/EmployeeOverlay';
import type { IDataTableField } from '@src/App/Components/Tables/DataTable/DataTable';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type Employee from '@src/Objects/Employee';
import { textContainsLowerCase } from '@src/Objects/Helper';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import EntitiesPage, { EViewType, type IEntitiesPageAction } from '../../Generic/EntitiesPage/EntitiesPage';
import type { IViewType } from '../../Generic/EntitiesPage/ViewTypeMenu';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
/**
 * Represents the EmployeesPage component.
 *
 * @param {EmployeesPageProps} props - The props object for the EmployeesPage component.
 * @returns {JSX.Element} The rendered EmployeesPage component.
 */
function EmployeesPage() {
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employees;
    const [selected, setSelected] = useState<number[]>([]);
    const entities = useSelector((state: AppState) => state.employees);
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);
    const orgUnits = useSelector((state: AppState) => state.orgUnits);
    const [activeRole, setActiveRole] = useState('hr');

    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);

    useEffect(() => {
        getEmployees().catch((ex) => {
            setLoadingError(ex);
        });
    }, []);

    const types: IViewType<Employee>[] = [
        {
            id: EViewType.CARD,
            title: langStrings.oftenUsed.cards,
            icon: <TableChart fontSize="small" />,
            renderContainer: (allEntities, innerEntries, width, selectedItems, tableSettings) => {
                return (
                    <Container>
                        {innerEntries.map((entity) => {
                            // Get org unit from Redux store
                            const OU = orgUnits
                                ? orgUnits.find((o) => o.employees.some((emp) => emp.id === entity.id))
                                : undefined;

                            const availability = entity.availability?.availability || 0;
                            const auslastung = 100 - availability;

                            const badges: EmployeeBadge[] = [];

                            // Top Performer bei Auslastung >= 80%
                            if (auslastung >= 80) {
                                badges.push({
                                    type: 'performance',
                                    label: 'Top Performer',
                                    color: 'warning',
                                    visible: ['manager', 'admin'],
                                });
                            }

                            // Verfügbar bei Auslastung <= 80%
                            if (auslastung <= 80) {
                                badges.push({
                                    type: 'status',
                                    label: 'Verfügbar',
                                    color: 'success',
                                    visible: ['employee', 'manager', 'admin'],
                                });
                            }

                            const employeeData: MvpEmployeeData = {
                                id: entity.id,
                                firstName: entity.firstName || 'Mock',
                                lastName: entity.lastName || 'Vorname',
                                pictureId: entity.pictureId || null,
                                role: entity.roles?.[0]?.title || 'Mitarbeiter',
                                orgUnit: OU?.title,
                                location: 'Deutschland',
                                email: entity.email || undefined,
                                availability,
                                badges,
                            };

                            return (
                                <MvpEmployeeCard
                                    key={entity.id}
                                    employee={employeeData}
                                    context="employees"
                                    userRole={
                                        activeRole === 'admin' ? 'admin' : activeRole === 'hr' ? 'manager' : 'employee'
                                    }
                                    onAction={(action, employeeId) => {
                                        console.log(`Action: ${action} for employee: ${employeeId}`);
                                        if (action === 'view-profile') {
                                            navigate(`/${ELinks.PEOPLE_EMPLOYEES}/${employeeId}`);
                                        } else if (action === 'schedule-meeting') {
                                            navigate(`/${ELinks.EMPLOYEE_TALKS}`);
                                        }
                                    }}
                                />
                            );
                        })}
                    </Container>
                );
            },
        },
        {
            id: EViewType.TABLE,
            title: langStrings.oftenUsed.table,
            icon: <TableRows fontSize="small" />,
            minWidth: 375,
            renderContainer: (allEntities, innerEntries, width, selectedItems, tableSettings) => {
                if (!tableSettings) return <div>Loading...</div>;

                const hiddenFieldsSet = new Set(
                    Object.keys(tableSettings.hiddenFields).filter((key) => tableSettings.hiddenFields[key]),
                );
                const visibleFields = tableFields.filter((f) => !hiddenFieldsSet.has(f.id));
                const sortedFields = visibleFields.sort((a, b) => {
                    const orderA = tableSettings.order.indexOf(a.id);
                    const orderB = tableSettings.order.indexOf(b.id);
                    return orderA - orderB;
                });

                return (
                    <Box sx={{ width: '100%' }}>
                        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <TableContainer>
                                <Table>
                                    <TableHead
                                        sx={{
                                            bgcolor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? alpha(theme.palette.primary.light, 0.15)
                                                    : alpha(theme.palette.primary.main, 0.12),
                                        }}
                                    >
                                        <TableRow>
                                            {sortedFields.map((field) => (
                                                <TableCell
                                                    key={field.id}
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: (theme) =>
                                                            theme.palette.mode === 'dark'
                                                                ? theme.palette.primary.light
                                                                : theme.palette.primary.dark,
                                                    }}
                                                >
                                                    {field.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {innerEntries.map((entity) => (
                                            <TableRow
                                                hover
                                                key={entity.id}
                                                onDoubleClick={() =>
                                                    navigate(`/${ELinks.PEOPLE_EMPLOYEES}/${entity.id}`)
                                                }
                                                sx={{ cursor: 'pointer' }}
                                            >
                                                {sortedFields.map((field) => (
                                                    <TableCell key={field.id}>
                                                        {field.id === 'id' && entity.id}
                                                        {field.id === 'firstName' && (
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <EmployeePicture
                                                                    employee={{
                                                                        id: entity.id,
                                                                        firstName: entity.firstName || 'Mock',
                                                                        lastName: entity.lastName || 'Vorname',
                                                                        pictureId: entity.pictureId || null,
                                                                    }}
                                                                    avatarSize={32}
                                                                />
                                                                <span
                                                                    onDoubleClick={() =>
                                                                        navigate(
                                                                            `/${ELinks.PEOPLE_EMPLOYEES}/${entity.id}`,
                                                                        )
                                                                    }
                                                                    style={{ fontWeight: 500 }}
                                                                >
                                                                    <EmployeeOverlay employeeId={entity.id}>
                                                                        {entity.firstName || 'Mock Vorname'}
                                                                    </EmployeeOverlay>
                                                                </span>
                                                            </Stack>
                                                        )}
                                                        {field.id === 'lastName' && (
                                                            <span
                                                                onDoubleClick={() =>
                                                                    navigate(`/${ELinks.PEOPLE_EMPLOYEES}/${entity.id}`)
                                                                }
                                                                style={{ fontWeight: 500 }}
                                                            >
                                                                <EmployeeOverlay employeeId={entity.id}>
                                                                    {entity.lastName || 'Mock Nachname'}
                                                                </EmployeeOverlay>
                                                            </span>
                                                        )}
                                                        {field.id === 'description' && (
                                                            <ExpandableText>
                                                                <FormatedTextInput value={entity.description || ''} />
                                                            </ExpandableText>
                                                        )}
                                                        {field.id === 'actions' && (
                                                            <Stack direction="row" spacing={1}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        navigate(
                                                                            `/${ELinks.PEOPLE_EMPLOYEES}/${entity.id}`,
                                                                        );
                                                                    }}
                                                                >
                                                                    <VisibilityIcon fontSize="small" />
                                                                </IconButton>
                                                            </Stack>
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Box>
                );
            },
        },
        {
            id: 'list' as any,
            title: 'Liste',
            icon: <TableRows fontSize="small" />,
            minWidth: 0,
            renderContainer: (allEntities, innerEntries) => (
                <Container>
                    {innerEntries.map((entity) => {
                        const OU = orgUnits
                            ? orgUnits.find((o) => o.employees.some((emp) => emp.id === entity.id))
                            : undefined;

                        return (
                            <Box
                                key={entity.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 2,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    cursor: 'pointer',
                                }}
                                onClick={() => navigate(`/${ELinks.PEOPLE_EMPLOYEES}/${entity.id}`)}
                            >
                                <EmployeePicture
                                    employee={{
                                        id: entity.id,
                                        firstName: entity.firstName || 'Mock',
                                        lastName: entity.lastName || 'Vorname',
                                        pictureId: entity.pictureId || null,
                                    }}
                                    avatarSize={40}
                                />
                                <Box sx={{ ml: 2, flex: 1 }}>
                                    <Typography variant="body1" fontWeight={500}>
                                        {entity.firstName || 'Mock'} {entity.lastName || 'Vorname'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {OU?.title || 'Unbekannt'}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Container>
            ),
        },
    ];

    const renderEmployeeStatsChips = () => {
        const externOU = orgUnits?.find((ou) => ou.title.toLowerCase() === 'extern');
        const totalEmployees = entities?.length || 0;
        const externalEmployees = externOU
            ? allEmployees?.filter((emp) => externOU.employees.some((ouEmp) => ouEmp.id === emp.id)).length || 0
            : 0;
        const internalEmployees = totalEmployees - externalEmployees;

        return (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                <Chip label={`${totalEmployees} Mitarbeiter`} color="primary" variant="outlined" size="small" />
                <Chip label={`${internalEmployees} Interne`} color="primary" variant="outlined" size="small" />
                <Chip label={`${externalEmployees} Externe`} color="primary" variant="outlined" size="small" />
            </Stack>
        );
    };

    const actions: IEntitiesPageAction[] = [];

    const tableFields: IDataTableField<any>[] = [
        { id: 'id', label: 'ID', align: 'left', disablePadding: false, type: 'string' },
        { id: 'firstName', label: langStrings.firstName, align: 'left', disablePadding: false, type: 'string' },
        { id: 'lastName', label: langStrings.lastName, align: 'left', disablePadding: false, type: 'string' },
        {
            id: 'description',
            label: langStrings.description,
            align: 'left',
            disablePadding: false,
            type: 'description',
        },
        { id: 'actions', label: 'Aktionen', align: 'right', disablePadding: false, type: 'string' },
    ];

    const roles: IMvpRole[] = [
        {
            id: 'hr',
            label: 'HR',
            icon: getAppIcon(EIconCategory.EMPLOYEES),
        },
        {
            id: 'admin',
            label: 'Admin',
            icon: getAppIcon(EIconCategory.SETTINGS),
        },
    ];

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'company-overview',
            label: 'Übersicht',
            icon: getAppIcon(EIconCategory.OVERVIEW),
            onClick: () => navigate(`/${ELinks.COMPANY}`),
            variant: 'primary',
        },
        {
            id: 'org-units',
            label: 'Abteilungsbereiche',
            icon: getAppIcon(EIconCategory.DEPARTMENTS),
            onClick: () => navigate(`/${ELinks.PEOPLE_ORGUNITS}`),
        },
    ];

    return (
        <>
            <MvpSubNav
                currentPageTitle="Mitarbeitende"
                roles={roles}
                activeRole={activeRole}
                onRoleChange={setActiveRole}
                quickActions={quickActions}
            />
            <EntitiesPage
                icon={getAppIcon(EIconCategory.EMPLOYEES)}
                caption="Übersicht"
                title="Mitarbeitende"
                subTitle="Übersicht aller Mitarbeiter mit ihren Profilen und Kompetenzen"
                entities={entities}
                selected={selected}
                loadingError={loadingError}
                tableFields={tableFields}
                tableId="employees-table"
                setSelected={setSelected}
                filter={(entity, selectedView, searchText) =>
                    selectedView.filter(entity) && textContainsLowerCase(searchText, entity.title)
                }
                views={[
                    {
                        id: 'all',
                        title: langStrings.oftenUsed.allEntries,
                        filter() {
                            return true;
                        },
                    },
                ]}
                types={types}
                actions={actions}
                chips={renderEmployeeStatsChips()}
            />
        </>
    );
}

export default EmployeesPage;
